import * as React from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
} from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { Box, Typography } from '@mui/material';
import { formatInr } from './leadFormatters';

echarts.use([LineChart, GridComponent, TooltipComponent, SVGRenderer]);

type TrendPoint = {
  period: string;
  gmv_inr: number;
  order_count: number;
};

type Props = {
  data?: TrendPoint[];
};

const LeadCredcoTrendChart: React.FC<Props> = ({ data = [] }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, { renderer: 'svg' });
    }
    const chart = chartInstance.current;

    if (!data.length) {
      chart.setOption({
        title: { text: 'No CredCo orders in last 12 months', left: 'center', top: 'middle', textStyle: { fontSize: 13, color: '#888' } },
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
      });
      return;
    }

    chart.setOption({
      title: { show: false },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const row = Array.isArray(params) ? params[0] : params;
          const idx = row?.dataIndex ?? 0;
          const point = data[idx];
          return `${point.period}<br/>GMV: ${formatInr(point.gmv_inr)}<br/>Orders: ${point.order_count}`;
        },
      },
      grid: { left: 56, right: 16, top: 12, bottom: 36 },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.period),
        axisLabel: { rotate: 35, fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => formatInr(v, true) },
      },
      series: [
        {
          name: 'GMV',
          type: 'line',
          smooth: true,
          data: data.map((d) => d.gmv_inr),
          areaStyle: { opacity: 0.12 },
          itemStyle: { color: '#2e7d32' },
          lineStyle: { width: 2 },
        },
      ],
    });

    const onResize = () => chart.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [data]);

  React.useEffect(
    () => () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    },
    []
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        CredCo GMV (12 months)
      </Typography>
      <Box ref={chartRef} sx={{ width: '100%', height: 220 }} />
    </Box>
  );
};

export default LeadCredcoTrendChart;
